import { INPUT_OF_FILE_TYPE, fileIdOf, fileStateOf, isSingleReadRow } from './samples'

// Display facts about a candidate sample (see groupRows) in the "Uploaded, not
// analysed yet" list and the file picker.

const TYPE_LABEL = { fastq: 'Paired-end FASTQ', ont_bam: 'ONT BAM', pacbio_bam: 'PacBio BAM' }
const VCF_LABEL = { cnv: 'VCF · CNV', sv: 'VCF · SV' }

// An upload still verifying this long after it arrived will not finish: it cannot be
// picked. Defined here, not imported from ready.js, which imports this module.
export const STUCK_VERIFYING_MS = 30 * 24 * 60 * 60 * 1000

const worst = (states) =>
  states.includes('unverified') ? 'unverified' : states.includes('verifying') ? 'verifying' : 'ready'

export const candidateInfo = (candidate, { now = Date.now() } = {}) => {
  const rows = candidate.rows || []
  const state = worst(rows.map(fileStateOf))
  const single = rows.some(isSingleReadRow)
  let typeLabel = TYPE_LABEL[candidate.fileType] || candidate.fileType
  if (candidate.fileType === 'vcf') typeLabel = VCF_LABEL[rows[0]?.variant_type] || 'VCF · SNV/Indel'
  if (candidate.fileType === 'fastq') {
    typeLabel = single ? 'FASTQ, R1 only' : 'Paired-end FASTQ'
    if (rows.length > 1) typeLabel += ` · ${rows.length} lanes`
  }
  const uploadedAt = rows.reduce((latest, row) => {
    const t = new Date(row.uploaded_at).getTime()
    return Number.isFinite(t) && t > latest ? t : latest
  }, 0)
  const stuck = state === 'verifying' && uploadedAt > 0 && now - uploadedAt > STUCK_VERIFYING_MS
  let disabledReason = ''
  if (single) disabledReason = 'R2 missing'
  else if (state === 'unverified') {
    disabledReason = rows.some((r) => r.refusal_reason) ? 'Could not be processed' : 'Not verified'
  } else if (stuck) disabledReason = 'Stuck verifying'
  return {
    state,
    single,
    stuck,
    typeLabel,
    uploadedAt,
    disabledReason,
    stateText: state === 'verifying' && !stuck ? 'Verifying' : '',
  }
}

/** The input a candidate feeds ('fastq' | 'ont' | 'pacbio' | 'vcf'), or null. */
export const candidateInput = (candidate) => INPUT_OF_FILE_TYPE[candidate?.fileType] || null

/** A VCF candidate's variant type ('snp_indel' | 'cnv' | 'sv'); null for other files. */
export const vcfKindOf = (candidate) => {
  if (candidate?.fileType !== 'vcf') return null
  const type = candidate.rows?.[0]?.variant_type
  return type === 'cnv' || type === 'sv' ? type : 'snp_indel'
}

/**
 * True when the candidate can join a batch of this workflow: Germline takes its
 * input, Somatic FASTQ takes FASTQ and Somatic VCF takes SNV/Indel VCFs. Group
 * analyses take finished analyses, not uploads.
 */
export const candidateFitsWorkflow = (candidate, workflow) => {
  const input = candidateInput(candidate)
  if (!input || !workflow) return false
  if (workflow.analysis === 'germline') return input === workflow.input
  if (workflow.analysis === 'somatic') {
    if (workflow.input === 'fastq') return input === 'fastq'
    if (workflow.input === 'vcf') return input === 'vcf' && vcfKindOf(candidate) === 'snp_indel'
  }
  return false
}

// A fixed month list: en-GB in newer browsers writes September as "Sept".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const shortDay = (ms) => {
  if (!ms) return ''
  const d = new Date(ms)
  const day = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return d.getFullYear() === new Date().getFullYear() ? day : `${day} ${d.getFullYear()}`
}

/** Ids of main files that already have an analysis, from /analysis/list rows. */
export const analysedFileIds = (analysisRows = []) => {
  const ids = new Set()
  analysisRows.forEach((row) => {
    const params = row?.analysis_parameters || {}
    if (params.fileId) ids.add(String(params.fileId))
    const annotation = params.annotation || {}
    ;(Array.isArray(annotation.allSelectedFiles) ? annotation.allSelectedFiles : []).forEach((id) =>
      ids.add(String(id)),
    )
    if (annotation.normalSampleId) ids.add(String(annotation.normalSampleId))
  })
  return ids
}

export const isCandidateAnalysed = (candidate, analysed) =>
  candidate.rows.some((row) => analysed.has(String(fileIdOf(row))))

/**
 * An upload in the "Uploaded, not analysed yet" list: no analysis yet, and it can
 * be analysed (verified or still verifying, but not stuck verifying). Failed and
 * R1-only uploads are left to the file picker, which says why.
 */
export const isAwaitingAnalysis = (candidate, analysed, { now } = {}) =>
  !isCandidateAnalysed(candidate, analysed) && !candidateInfo(candidate, { now }).disabledReason

/** How many uploads wait for an analysis, by input: { fastq, ont, pacbio, vcf }. */
export const countWaiting = (candidates = [], analysed = new Set(), { now } = {}) => {
  const counts = { fastq: 0, ont: 0, pacbio: 0, vcf: 0 }
  candidates.forEach((candidate) => {
    const input = candidateInput(candidate)
    if (input && isAwaitingAnalysis(candidate, analysed, { now })) counts[input] += 1
  })
  return counts
}
